import { api } from './client'
import type { AuthGuard } from '@/lib/jwt'
import type {
  TotpConfirmResponse,
  TotpDeviceListResponse,
  TotpSetupResponse,
  TotpStepUpStatus,
} from '@/types/two-factor'

export interface TwoFactorContext {
  guard: AuthGuard
}

function prefix(context: TwoFactorContext): '/api/admin' | '/api/institution' {
  return context.guard === 'admin' ? '/api/admin' : '/api/institution'
}

export async function listTotpDevices(
  context: TwoFactorContext
): Promise<TotpDeviceListResponse> {
  return api.get<TotpDeviceListResponse>(`${prefix(context)}/auth/2fa/devices`, {
    requireAuth: true,
  })
}

export async function startTotpDevice(
  input: { password: string; name?: string; code?: string },
  context: TwoFactorContext
): Promise<TotpSetupResponse> {
  const payload: { password: string; name?: string; code?: string } = {
    password: input.password,
    name: input.name?.trim() || undefined,
  }
  if (input.code) {
    payload.code = input.code
  }

  return api.post<TotpSetupResponse>(
    `${prefix(context)}/auth/2fa/devices`,
    payload,
    { requireAuth: true }
  )
}

export async function confirmTotpDevice(
  deviceId: number,
  code: string,
  context: TwoFactorContext
): Promise<TotpConfirmResponse> {
  return api.post<TotpConfirmResponse>(
    `${prefix(context)}/auth/2fa/devices/${deviceId}/confirm`,
    { code },
    { requireAuth: true }
  )
}

export async function revokeTotpDevice(
  deviceId: number,
  input: { password: string; code?: string },
  context: TwoFactorContext
): Promise<{ message: string }> {
  return api.post<{ message: string }>(
    `${prefix(context)}/auth/2fa/devices/${deviceId}/revoke`,
    {
      password: input.password,
      code: input.code || undefined,
    },
    { requireAuth: true }
  )
}

export async function getTotpStepUpStatus(
  context: TwoFactorContext
): Promise<TotpStepUpStatus> {
  return api.get<TotpStepUpStatus>(`${prefix(context)}/auth/2fa/step-up`, {
    requireAuth: true,
    cache: 'no-store',
  })
}

export async function verifyTotpStepUp(
  code: string,
  context: TwoFactorContext
): Promise<TotpStepUpStatus> {
  return api.post<TotpStepUpStatus>(
    `${prefix(context)}/auth/2fa/step-up`,
    { code },
    { requireAuth: true }
  )
}
